
import { Button, Card, Empty, InputNumber, Select, Space, Table } from "@arco-design/web-react";
import { Number, Currency } from "@/components/Numeric";
import { useCallback, useEffect, useState } from "react";
import { SOAPI, CustomerAPI } from "@/apis/sales";
import { ItemAPI } from "@/apis/material";
import { FileUtil } from "@/utils";
import { SOStatus } from "@/enums/sales";
import _ from "lodash"

export const SalesReport = () => {
    const [customers, setCustomers] = useState([]);
    const [selectedCustomers, setSelectedCustomers] = useState([]);
    const [items, setItems] = useState([]);
    const [selectedItems, setSelectedItems] = useState([]);
    const [data, setData] = useState([]);
    const [pagination, setPagination] = useState({
        current: 1,
        total: 0,
    });
    const [month, setMonth] = useState(3);
    const [sortState, setSortState] = useState({ field: 'saleOrder.soDate', direction: 'descend' });

    const fieldMapping = {
        'saleOrder.soDate': 'soDate',
        'saleOrder.customerName': 'customerName',
        'itemName': 'itemName',
    };

    const getCustomersAndItems = useCallback(async () => {
        const customers = await CustomerAPI.actives();
        const sortedCustomers = _.sortBy(customers, 'customerName');
        setCustomers(sortedCustomers);
        const items = await ItemAPI.actives();
        const sortedItems = _.sortBy(items, 'itemName');
        setItems(sortedItems);
    }, []);

    const getData = async (page, sort) => {
        const currentSort = sort ?? sortState;
        const mappedField = currentSort.field ? fieldMapping[currentSort.field] : undefined;
        const result = await SOAPI.getRecentSales({
            page: page ?? 1,
            lastMonth: month,
            customerIds: selectedCustomers,
            itemIds: selectedItems,
            excludeStatuses: [SOStatus.Voided],
            orderBy: mappedField,
            orderDesc: currentSort.direction === 'descend',
        });
        setData(result.records);
        setPagination((pagination) => {
            return {
                ...pagination,
                total: result.total,
            }
        });
    };

    useEffect(() => {
        getCustomersAndItems();
    }, [getCustomersAndItems]);

    return (
        <Card title="Sales Report"
            extra={<Space>
                <span>Date Range: Last</span>
                <InputNumber
                    style={{ width: 45 }}
                    hideControl
                    min={1}
                    value={month}
                    onChange={(val) => {
                        setMonth(val);
                    }}
                />
                <span>Month</span>
                <Select
                    mode="multiple"
                    showSearch
                    allowClear
                    placeholder="Select customers..."
                    maxTagCount={1}
                    style={{ width: 200 }}
                    value={selectedCustomers}
                    filterOption={(inputValue, option) =>
                        option.props.children.toLowerCase().indexOf(inputValue.toLowerCase()) >= 0
                    }
                    onChange={(value) => {
                        setSelectedCustomers(value);
                    }}
                    options={customers.map(item => ({
                        label: item.customerName,
                        value: item.id,
                    }))}
                />
                <Select
                    mode="multiple"
                    showSearch
                    allowClear
                    placeholder="Select items..."
                    maxTagCount={1}
                    style={{ width: 200 }}
                    value={selectedItems}
                    filterOption={(inputValue, option) =>
                        option.props.children.toLowerCase().indexOf(inputValue.toLowerCase()) >= 0
                    }
                    onChange={(value) => {
                        setSelectedItems(value);
                    }}
                    options={items.map(item => ({
                        label: item.itemName,
                        value: item.id
                    }))}
                />
                <Button type="primary" onClick={() => {
                    getData();
                }}>Search</Button>
                <Button type="primary" onClick={async () => {
                    const mappedField = sortState.field ? fieldMapping[sortState.field] : undefined;
                    const res = await SOAPI.exportRecentSales({
                        lastMonth: month,
                        customerIds: selectedCustomers,
                        itemIds: selectedItems,
                        excludeStatuses: [SOStatus.Voided],
                    }, {
                        orderBy: mappedField,
                        orderDesc: sortState.direction === 'descend',
                    });
                    FileUtil.download(res.data, "sales_report.xlsx");
                }}>Export</Button>
            </Space>}>

            <Table columns={[
                {
                    title: 'Customer',
                    dataIndex: 'saleOrder.customerName',
                },
                {
                    title: 'Item Name',
                    dataIndex: 'itemName',
                },
                {
                    title: 'SO#',
                    dataIndex: 'saleOrder.soNo',
                    render: (col, record) => <a href={`/workspace/sales/so/${record.soId}`}>{record.saleOrder.soNo}</a>,
                },
                {
                    title: 'SO Date',
                    dataIndex: 'saleOrder.soDate',
                    sorter: true,
                },
                {
                    title: 'Qty',
                    render: (col, record) => <Number value={record.quantity} />
                },
                {
                    title: 'Price',
                    render: (col, record) => <Currency value={record.rate} />
                },
                {
                    title: 'Amount',
                    render: (col, record) => <Currency value={record.amount} />
                },
            ]}
                rowKey="id"
                hover={false}
                scroll={{ y: 'calc(100vh - 300px)' }}
                data={data}
                noDataElement={<Empty description="Select filters and Click Search to Load Data" />}
                pagination={pagination}
                onChange={(pagination, sorter) => {
                    setPagination(pagination);
                    const newSort = {
                        field: sorter?.field,
                        direction: sorter?.direction,
                    };
                    setSortState(newSort);
                    getData(pagination.current, newSort);
                }} />
        </Card>
    );
}
